/**
 * State machine for managing continuous scan screen transitions
 * Handles navigation between:
 * - Screen B: Active count display (scanning)
 * - Screen C: Summary results (paused)
 * - Screen D: Category details (category selected)
 */
import logging from '@sstdev/lib_logging';

export const SCREEN_STATES = {
    SCANNING: 'SCANNING',        // Screen B - actively counting
    SUMMARY: 'SUMMARY',          // Screen C - paused, showing summary
    CATEGORY_DETAILS: 'CATEGORY_DETAILS' // Screen D - viewing category details
};

export const ACTIONS = {
    // Screen B transitions
    PAUSE: 'PAUSE',              // Trigger released - show summary
    RESUME: 'RESUME',            // Trigger pressed - back to counting

    // Screen C transitions
    SELECT_CATEGORY: 'SELECT_CATEGORY',     // Tap category - go to details
    CLOSE_SCAN: 'CLOSE_SCAN',               // Cancel/close entire modal
    SUBMIT: 'SUBMIT',                       // Submit results

    // Screen D transitions
    CLOSE_CATEGORY_DETAILS: 'CLOSE_CATEGORY_DETAILS',  // Back to summary
    REMOVE_ENTRY: 'REMOVE_ENTRY',                      // Remove specific entry
    RESTORE_ENTRY: 'RESTORE_ENTRY',                    // Restore a previously removed entry

    // Data updates
    UPDATE_SUMMARY: 'UPDATE_SUMMARY',       // Update summary data
    UPDATE_COUNT: 'UPDATE_COUNT',           // Update tag count
    UPDATE_CONNECTION_STATUS: 'UPDATE_CONNECTION_STATUS' // Update MQTT connection status
};

/**
 * Initial state for continuous scan
 */
export const getInitialState = () => ({
    currentScreen: SCREEN_STATES.SCANNING,
    selectedCategory: null,
    readCounts: {
        total: 0,
        matched: 0,
        unknown: 0,
        duplicates: 0,
        errors: 0
    },
    summaryData: null,
    isSubmitting: false,
    eventHistory: [],
    manuallyRemovedCount: 0,
    // Items the user has manually removed, preserved here so they can be restored.
    // Each entry is the original item object plus an `originalCategory` field.
    removedItems: [],
    // null until the first continuousScanConnectionChange event arrives - one of 'connecting',
    // 'connected', 'disconnected', 'failed' (see ContinuousScanModule.java's emitConnectionEvent).
    connectionStatus: null
});

/**
 * Merges a new batch of rows onto the rows already accumulated across earlier ResultEvents,
 * keyed by tagEpc. A tag re-reported in a later batch (e.g. re-scanned again during a later
 * trigger hold) replaces its earlier entry instead of appending a duplicate, so
 * CategoryDetailsScreen's list stays one row per distinct tag - matching the cumulative
 * foundCount/movedCount/unknownCount the backend already reports.
 * @param {Array} existing
 * @param {Array} incoming
 * @returns {Array}
 */
function mergeRowsByTagEpc(existing, incoming) {
    if (!incoming || incoming.length === 0) return existing || [];
    const merged = [...(existing || [])];
    const indexByTagEpc = new Map(merged.map((row, index) => [row.tagEpc, index]));
    for (const row of incoming) {
        const existingIndex = indexByTagEpc.get(row.tagEpc);
        if (existingIndex != null) {
            merged[existingIndex] = row;
        } else {
            indexByTagEpc.set(row.tagEpc, merged.length);
            merged.push(row);
        }
    }
    return merged;
}

const SUMMARY_CATEGORIES = ['found', 'moved', 'unknown', 'unfound'];

/**
 * A tag's category is not fixed - the backend can move a tag it previously reported as e.g.
 * "found" into "moved" or "unknown" once a later batch finishes matching it against the full
 * inventory (that's why foundCount can drop from 11 to 0 between batches while movedCount and
 * unknownCount pick up the difference). mergeRowsByTagEpc only appends/replaces within a single
 * category's array, so without this step a reclassified tag's row would keep piling up in its
 * old category's list forever, out of sync with the cumulative counts. So first strip any tagEpc
 * that appears anywhere in THIS batch out of every category's existing rows, then merge each
 * category's incoming rows back in - leaving each tag in only its latest reported category.
 * @param {Object.<string, Array<*>>} existingByCategory summaryData from state (found/moved/unknown/unfound arrays)
 * @param {Object.<string, Array<*>>} incomingByCategory this batch's summary payload
 * @returns {Object.<string, Array<*>>} merged found/moved/unknown/unfound arrays
 */
function mergeCategorizedSummary(existingByCategory, incomingByCategory) {
    const reclassified = new Set();
    for (const category of SUMMARY_CATEGORIES) {
        for (const row of incomingByCategory[category] || []) {
            reclassified.add(row.tagEpc);
        }
    }
    /** @type {Object.<string, Array<*>>} */
    const merged = {};
    for (const category of SUMMARY_CATEGORIES) {
        const existingRows = (existingByCategory?.[category] || []).filter(row => !reclassified.has(row.tagEpc));
        merged[category] = mergeRowsByTagEpc(existingRows, incomingByCategory[category]);
    }
    return merged;
}

/**
 * State reducer for continuous scan transitions
 * @param {Object.<string,*>} state Current state
 * @param {Object} action Action to dispatch
 * @returns {Object} New state
 */
export function continuousScanReducer(state, action) {
    switch (action.type) {
        case ACTIONS.PAUSE:
            return {
                ...state,
                currentScreen: SCREEN_STATES.SUMMARY
            };

        case ACTIONS.RESUME:
            // summaryData is intentionally NOT cleared here. If the next hold cycle produces no
            // new backend-categorized reads (e.g. only duplicates, nothing new to process), there
            // is no continuousScanResultUpdate to refresh it - clearing it would leave the
            // Summary screen with nothing but native's own uncategorized hold-count guess, which
            // stomps the last accurate found/moved/unknown breakdown with an all-"found" total.
            return {
                ...state,
                currentScreen: SCREEN_STATES.SCANNING,
                selectedCategory: null
            };

        case ACTIONS.SELECT_CATEGORY:
            logging.debug('[STATE_MACHINE] SELECT_CATEGORY action - changing to CATEGORY_DETAILS, selected:', action.payload?.category);
            return {
                ...state,
                currentScreen: SCREEN_STATES.CATEGORY_DETAILS,
                selectedCategory: action.payload?.category || null
            };

        case ACTIONS.CLOSE_CATEGORY_DETAILS:
            return {
                ...state,
                currentScreen: SCREEN_STATES.SUMMARY,
                selectedCategory: null
            };

        case ACTIONS.CLOSE_SCAN:
            return {
                ...state,
                currentScreen: null,
                selectedCategory: null
            };

        case ACTIONS.SUBMIT:
            return {
                ...state,
                isSubmitting: true
            };

        case ACTIONS.UPDATE_SUMMARY: {
            const incomingSummary = action.payload?.summary || null;

            // readCounts.total is intentionally left untouched here - it is owned exclusively by
            // UPDATE_COUNT (see continuousScanEventParsing.js's getNextReadCountsFromEvent, which
            // already derives it from this same payload, monotonically). This reducer branch used
            // to also set readCounts.total from incomingSummary.foundCount alone (ignoring moved/
            // unknown) and, worse, would let it REGRESS whenever a backend batch reported fewer
            // reads than a faster scan had already produced - that's the root cause of Screen B
            // (live count) and Screen C/D (this summary's found+moved+unknown) disagreeing after a
            // fast scan: the backend classifies reads in ~1s batches (silo/stream's
            // BATCH_WINDOW_MS) and can still be behind the reader when the trigger is released.
            //
            // foundCount/movedCount/unknownCount are cumulative running totals from the backend,
            // but the found/moved/unknown arrays each ResultEvent carries only contain that
            // batch's new rows (see silo/stream/src/batchProcessor/worker.ts - the counters are
            // HINCRBY'd in Redis, but classifyReads' per-batch arrays are published as-is). A
            // straight replace here would wipe every earlier batch's rows out of
            // CategoryDetailsScreen's list every time a new batch arrives, even though the counts
            // stay correctly cumulative - so merge onto whatever rows are already accumulated,
            // deduplicating by tagEpc AND moving a tag out of its old category if this batch
            // reclassifies it into a different one (see mergeCategorizedSummary above - a tag
            // reported "found" in one batch can come back "moved"/"unknown" in the next once the
            // backend finishes matching it against the full inventory).
            //
            // Exception: if the summary currently in state is native's own placeholder guess (see
            // ContinuousScanScreen.native.js's continuousScanSummary handler - it labels every held
            // read as "found" before the backend has categorized anything), it must not be merged
            // onto at all. Its rows are fabricated, not a real earlier batch, so once real backend
            // data arrives it fully replaces the placeholder instead of merging with it - otherwise
            // every tag the placeholder guessed "found" that the real batch didn't happen to also
            // mention would wrongly linger in the found list forever.
            const mergedSummary = incomingSummary
                ? (state.summaryData?.isPlaceholder
                    ? { ...incomingSummary }
                    : {
                        ...incomingSummary,
                        ...mergeCategorizedSummary(state.summaryData, incomingSummary)
                    })
                : null;

            // Backend is authoritative for Removed state. When ResultEvent carries removed rows
            // and removedCount, mirror them directly instead of inferring from frontend deltas.
            const hasBackendRemovedRows = Array.isArray(incomingSummary?.removed);
            const hasBackendRemovedCount = Number.isFinite(incomingSummary?.removedCount);
            const previousRemovedByTagEpc = new Map((state.removedItems || []).map(item => [item.tagEpc, item]));
            const nextRemovedItems = hasBackendRemovedRows
                ? incomingSummary.removed.map(item => {
                    const previous = previousRemovedByTagEpc.get(item.tagEpc);
                    const originalCategory = item.originalCategory || item.category || previous?.originalCategory;
                    return originalCategory ? { ...item, originalCategory } : item;
                })
                : (state.removedItems || []);
            const nextManuallyRemovedCount = hasBackendRemovedCount
                ? Math.max(0, Number(incomingSummary.removedCount) || 0)
                : (state.manuallyRemovedCount || 0);

            return {
                ...state,
                summaryData: mergedSummary,
                removedItems: nextRemovedItems,
                manuallyRemovedCount: nextManuallyRemovedCount,
                eventHistory: [
                    ...(state.eventHistory || []),
                    {
                        type: 'SUMMARY_UPDATE',
                        timestamp: new Date().toISOString(),
                        data: action.payload?.summary
                    }
                ]
            };
        }

        case ACTIONS.UPDATE_COUNT:
            return {
                ...state,
                readCounts: {
                    ...state.readCounts,
                    ...action.payload?.counts
                },
                eventHistory: [
                    ...(state.eventHistory || []),
                    {
                        type: 'COUNT_UPDATE',
                        timestamp: new Date().toISOString(),
                        data: action.payload?.counts
                    }
                ]
            };

        case ACTIONS.UPDATE_CONNECTION_STATUS:
            return {
                ...state,
                connectionStatus: action.payload?.status ?? null
            };

        case ACTIONS.REMOVE_ENTRY: {
            // Optimistically remove the entry from the category array/count it came from so
            // CategoryDetailsScreen's list updates immediately, without waiting on (or requiring)
            // a server ResultEvent - RemoveEntry has no server acknowledgement per
            // data-contracts.md#37-removeentry.
            const { tagEpc, category } = action.payload || {};
            let summaryData = state.summaryData;
            let manuallyRemovedCount = state.manuallyRemovedCount || 0;
            let removedItems = state.removedItems || [];
            if (summaryData && category && Array.isArray(summaryData[category])) {
                const itemToRemove = summaryData[category].find(item => item.tagEpc === tagEpc);
                const filtered = summaryData[category].filter(item => item.tagEpc !== tagEpc);
                if (filtered.length !== summaryData[category].length) {
                    const removedEntry = { ...itemToRemove, originalCategory: category };
                    summaryData = {
                        ...summaryData,
                        [category]: filtered,
                        [`${category}Count`]: Math.max(0, (summaryData[`${category}Count`] || 0) - 1),
                        // Keep summaryData.removed in sync so CategoryDetailsScreen's Removed tab
                        // updates immediately when the backend has already provided a removed array.
                        ...(Array.isArray(summaryData.removed) && {
                            removed: [...summaryData.removed, removedEntry],
                            removedCount: (summaryData.removedCount || 0) + 1
                        })
                    };
                    // A user-initiated removal lowers summaryData's category counts the same way a
                    // backend batch lagging behind would, but it isn't backend lag - it's a deliberate
                    // exclusion. ContinuousScanContextProvider's isReconciling compares readCounts.total
                    // (monotonic, never decreases) against summaryData's counts, so without tracking
                    // removals separately and feeding them back in, every removal would permanently
                    // widen that gap and isReconciling would latch true forever with nothing left to
                    // ever close it (see PR review on ContinuousScanContextProvider.js).
                    manuallyRemovedCount += 1;
                    removedItems = [...removedItems, removedEntry];
                }
            }
            return {
                ...state,
                summaryData,
                manuallyRemovedCount,
                removedItems,
                eventHistory: [
                    ...(state.eventHistory || []),
                    {
                        type: 'REMOVE_ENTRY',
                        timestamp: new Date().toISOString(),
                        data: action.payload
                    }
                ]
            };
        }

        case ACTIONS.RESTORE_ENTRY: {
            const { tagEpc } = action.payload || {};
            const removedItems = state.removedItems || [];
            const itemToRestore = removedItems.find(item => item.tagEpc === tagEpc);
            // No-op if the item isn't in the removed list or there's no summary to restore into.
            if (!itemToRestore || !state.summaryData) return state;

            const { originalCategory, category, ...itemData } = itemToRestore;
            const restoreCategory = originalCategory || category;
            if (!restoreCategory) return state;
            const nextRemovedItems = removedItems.filter(item => item.tagEpc !== tagEpc);
            // Capture the existing backend-provided removed array (if any) so TypeScript can
            // narrow the type for the filter inside the spread below.
            const existingBackendRemoved = Array.isArray(state.summaryData.removed) ? state.summaryData.removed : null;
            const summaryData = {
                ...state.summaryData,
                [restoreCategory]: [...(state.summaryData[restoreCategory] || []), itemData],
                [`${restoreCategory}Count`]: (state.summaryData[`${restoreCategory}Count`] || 0) + 1,
                // Keep summaryData.removed in sync so the Removed tab stops showing the item
                // immediately when the backend has already provided a removed array.
                ...(existingBackendRemoved && {
                    removed: existingBackendRemoved.filter((/** @type {*} */ item) => item.tagEpc !== tagEpc),
                    removedCount: Math.max(0, (state.summaryData.removedCount || 0) - 1)
                })
            };
            return {
                ...state,
                summaryData,
                removedItems: nextRemovedItems,
                manuallyRemovedCount: Math.max(0, (state.manuallyRemovedCount || 0) - 1),
                eventHistory: [
                    ...(state.eventHistory || []),
                    {
                        type: 'RESTORE_ENTRY',
                        timestamp: new Date().toISOString(),
                        data: action.payload
                    }
                ]
            };
        }

        default:
            return state;
    }
}

/**
 * Utility to check valid screen transitions
 * @param {string} fromScreen Current screen
 * @param {string} action Action being performed
 * @returns {boolean} True if transition is valid
 */
export function isValidTransition(fromScreen, action) {
    const validTransitions = {
        [SCREEN_STATES.SCANNING]: [
            ACTIONS.PAUSE,
            ACTIONS.CLOSE_SCAN,
            ACTIONS.UPDATE_COUNT,
            ACTIONS.UPDATE_CONNECTION_STATUS
        ],
        [SCREEN_STATES.SUMMARY]: [
            ACTIONS.RESUME,
            ACTIONS.SELECT_CATEGORY,
            ACTIONS.CLOSE_SCAN,
            ACTIONS.SUBMIT,
            ACTIONS.UPDATE_SUMMARY,
            ACTIONS.UPDATE_CONNECTION_STATUS
        ],
        [SCREEN_STATES.CATEGORY_DETAILS]: [
            ACTIONS.CLOSE_CATEGORY_DETAILS,
            ACTIONS.REMOVE_ENTRY,
            ACTIONS.RESTORE_ENTRY,
            ACTIONS.UPDATE_CONNECTION_STATUS
        ]
    };

    return validTransitions[fromScreen]?.includes(action) ?? false;
}

/**
 * Get the appropriate screen component based on state
 * @param {string} screenState Current screen state
 * @returns {string} Screen identifier
 */
export function getScreenForState(screenState) {
    switch (screenState) {
        case SCREEN_STATES.SCANNING:
            return 'ContinuousScanScreen';
        case SCREEN_STATES.SUMMARY:
            return 'SummaryScreen';
        case SCREEN_STATES.CATEGORY_DETAILS:
            return 'CategoryDetailsScreen';
        default:
            return null;
    }
}
